import React, { ReactNode, useState, useEffect, useContext } from 'react'
import { ColorFormat, ColorVariable } from '../../../../../../types'
import PopoverTrigger from './PopoverTrigger'
import { PopoverContent } from './PopoverContent'
import { StyledPopover, TriggerContainer } from './ColorVariablePopover.styles'
import { useVersioning } from '../../../../../../../versioning'
import { useGetComponentWithUuid } from '../../../useGetComponentWithUuid'
import { formatPath } from '../../../../../utils'
import { HoverFillColorContext } from '../../../sections/Style/Fills/HoverFillColor'

/**
 * UTILS
 */
const colorVariableInfo = (
  colorVariableName: string,
  colorVariableSourceLibraryName?: string | null
) => {
  const groups = colorVariableName.split('/')

  const name = groups.length ? groups[groups.length - 1] : colorVariableName

  // Remove color variable name from path (removes last item)
  groups.splice(-1, 1)

  const library = colorVariableSourceLibraryName
    ? colorVariableSourceLibraryName.trim()
    : 'This Document'

  const path = groups.length ? `${library}/${formatPath(groups)}` : library

  return [name, path] as const
}

export const ColorVariablePopover: React.FCC<
  ColorVariable & {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
    copyValue: string
    dropdown: ReactNode
    noLabel?: boolean
  }
> = ({
  red,
  green,
  blue,
  alpha = 1,
  colorVariableID,
  colorVariableRemoteID,
  colorVariableName,
  colorVariableSourceLibraryID,
  colorVariableSourceLibraryName,
  noLabel = false,
}) => {
  let [isPopoverOpen, setPopoverOpen] = useState(false)
  const context = useContext(HoverFillColorContext)

  // Every time the color variable changes the popover should hide
  useEffect(() => {
    setPopoverOpen(false)
  }, [colorVariableName])

  if (noLabel) {
    isPopoverOpen = context.isPopoverOpen
    setPopoverOpen = (value: boolean | ((prevState: boolean) => boolean)) => {
      const newValue =
        typeof value === 'function' ? value(context.isPopoverOpen) : value
      context.setPopoverOpen(newValue)
    }
  }

  const versionContext = useVersioning()
  const { share: shareFromContext } = versionContext
  const { currentVersion } = (!versionContext.loading && versionContext) || {}

  // isForeign should be part of Color type, but for now it can be derived
  const isForeign = !!(
    colorVariableSourceLibraryID && colorVariableSourceLibraryName
  )

  const {
    shareID,
    externalShareName,
    componentUnavailable,
    searchParams,
    component,
  } = useGetComponentWithUuid({
    componentId: isForeign ? colorVariableRemoteID : colorVariableID,
    externalDocumentId: colorVariableSourceLibraryID,
    externalDocumentName: colorVariableSourceLibraryName,
    isForeign,
    shareIdentifier: shareFromContext.identifier,
    currentVersion,
  })

  const [name, path] = colorVariableInfo(
    component?.name || colorVariableName,
    // externalShareName has wrong value when there is an error with the query,
    // that's why we need to provide a fallback using colorVariableSourceLibraryName
    componentUnavailable ? colorVariableSourceLibraryName : externalShareName
  )

  const handlePopoverClose = () => setPopoverOpen(false)

  return (
    <StyledPopover
      visible={isPopoverOpen}
      placement="left"
      spacing="16px"
      onClickOutside={handlePopoverClose}
      noLabel={noLabel}
      popup={
        <PopoverContent
          color={{ red, green, blue, alpha }}
          name={name}
          path={path}
          shareID={shareID}
          componentUnavailable={componentUnavailable}
          searchParams={searchParams}
          isForeign={isForeign}
          handleClose={handlePopoverClose}
        />
      }
      data-testid="color-variable-popover"
    >
      <TriggerContainer>
        <PopoverTrigger
          red={red}
          green={green}
          blue={blue}
          alpha={alpha}
          name={name}
          path={path}
          openPopover={() => setPopoverOpen(!isPopoverOpen)}
        />
      </TriggerContainer>
    </StyledPopover>
  )
}
